import { useCallback, useEffect, useState, type FormEvent } from "react";
import { AppShell } from "../components/AppShell";
import { Icon } from "../components/Icon";
import {
  convertPublishedCatalogImage,
  deleteNetwork,
  fetchCatalogSnapshot,
  saveNetwork,
  uploadCatalogImage,
} from "../lib/catalogApi";
import type { Network } from "../lib/catalogTypes";
import { CURATED_NETWORK_ICONS } from "../lib/networkIconsCurated";
import { isVectorAssetURL } from "../lib/catalogImages";
import { fetchSimpleIconFile } from "../lib/simpleIconsImport";

export function NetworksPage() {
  const [rows, setRows] = useState<Network[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<Network> | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setRows((await fetchCatalogSnapshot()).networks);
  }, []);

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : "Load failed"));
  }, [reload]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing?.slug || !editing.name) return;
    setBusy(true);
    setError(null);
    try {
      await saveNetwork({
        recordName: editing.recordName,
        slug: editing.slug,
        name: editing.name,
        logoURL: editing.logoURL ?? null,
        sortOrder: Number(editing.sortOrder ?? 0),
        isActive: editing.isActive !== false,
      });
      setEditing(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function cacheNetworkIcon(networkSlug: string) {
    const source = CURATED_NETWORK_ICONS.find((item) => item.networkSlug === networkSlug);
    if (!source) throw new Error(`No curated SVG source is mapped for ${networkSlug}.`);
    const file = await fetchSimpleIconFile(source.svgURL, source.brandHex);
    return uploadCatalogImage(file, "network");
  }

  async function onCacheEditingIcon() {
    if (!editing?.slug) return;
    setBusy(true);
    setError(null);
    try {
      const logoURL = await cacheNetworkIcon(editing.slug);
      setEditing((current) => (current ? { ...current, logoURL } : current));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network icon import failed");
    } finally {
      setBusy(false);
    }
  }

  async function onCacheKnownNetworks() {
    setBusy(true);
    setError(null);
    try {
      for (const [index, source] of CURATED_NETWORK_ICONS.entries()) {
        const existing = rows.find((row) => row.slug === source.networkSlug);
        const logoURL = await cacheNetworkIcon(source.networkSlug);
        await saveNetwork({
          recordName: existing?.recordName,
          slug: source.networkSlug,
          name: existing?.name ?? source.displayName,
          logoURL,
          sortOrder: existing?.sortOrder ?? index * 10,
          isActive: existing?.isActive !== false,
        });
      }
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network icon import failed");
    } finally {
      setBusy(false);
    }
  }

  async function convertExistingLogo(row: Network) {
    if (!row.logoURL || !isVectorAssetURL(row.logoURL)) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const logoURL = await convertPublishedCatalogImage(row.logoURL, "network");
      await saveNetwork({ ...row, logoURL });
      setMessage(`Converted ${row.name} to an app-ready PNG.`);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "SVG conversion failed.");
    } finally {
      setBusy(false);
    }
  }

  async function convertAllExistingLogos() {
    const vectors = rows.filter((row) => row.logoURL && isVectorAssetURL(row.logoURL));
    if (vectors.length === 0) {
      setMessage("No network SVG files need conversion.");
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      let converted = 0;
      for (const row of vectors) {
        const logoURL = await convertPublishedCatalogImage(row.logoURL!, "network");
        await saveNetwork({ ...row, logoURL });
        converted += 1;
      }
      setMessage(`Converted ${converted} network SVG file(s) to app-ready PNG.`);
      await reload();
    } catch (err) {
      setError(`${err instanceof Error ? err.message : "SVG conversion failed."} Some records may already be converted.`);
      await reload().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell>
      <main className="page">
        <div className="page-header">
          <div>
            <span className="overline">Catalog</span>
            <h1>Networks</h1>
            <p>Visa, Mastercard, JCB, … — slug should match iOS CardNetwork.</p>
          </div>
          <button
            className="btn btn-brass"
            type="button"
            disabled={busy || !rows.some((row) => isVectorAssetURL(row.logoURL))}
            onClick={() => void convertAllExistingLogos()}
          >
            <Icon name="refresh" size={15} />
            Convert all SVGs
          </button>
          <button
            className="btn btn-brass"
            type="button"
            disabled={busy}
            onClick={() => void onCacheKnownNetworks()}
          >
            <Icon name="upload" size={15} />
            {busy ? "Caching…" : "Cache known icons → PNG"}
          </button>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => setEditing({ slug: "", name: "", sortOrder: 0, isActive: true })}
          >
            <Icon name="plus" size={15} />
            Add network
          </button>
        </div>
        {error && <div className="banner error">{error}</div>}
        {message && <div className="banner success">{message}</div>}
        {editing && (
          <form className="panel form-grid" onSubmit={(e) => void onSubmit(e)}>
            <label>
              Slug
              <input
                value={editing.slug ?? ""}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                required
                disabled={Boolean(editing.recordName)}
              />
            </label>
            <label>
              Name
              <input
                value={editing.name ?? ""}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                required
              />
            </label>
            <label>
              Sort
              <input
                type="number"
                value={editing.sortOrder ?? 0}
                onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
              />
            </label>
            <label className="full">
              Logo URL
              <input
                value={editing.logoURL ?? ""}
                onChange={(e) => setEditing({ ...editing, logoURL: e.target.value || null })}
              />
            </label>
            <div className="row-actions full">
              <button
                className="btn btn-brass"
                type="button"
                disabled={
                  busy ||
                  !CURATED_NETWORK_ICONS.some((item) => item.networkSlug === editing.slug)
                }
                onClick={() => void onCacheEditingIcon()}
              >
                <Icon name="upload" size={15} />
                Fetch mapped SVG → R2
              </button>
              <span className="field-hint" style={{ alignSelf: "center" }}>
                Visa, Mastercard, Amex, Discover, and JCB use pinned Simple Icons assets.
              </span>
            </div>
            <label>
              Logo file
              <input
                type="file"
                accept=".svg,.png,.jpg,.jpeg,.webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  void uploadCatalogImage(file, "network")
                    .then((url) => setEditing((prev) => (prev ? { ...prev, logoURL: url } : prev)))
                    .catch((err) => setError(err instanceof Error ? err.message : "Upload failed"));
                }}
              />
              <span className="field-hint">
                SVG becomes a 256px PNG — the app cannot draw vectors.
              </span>
            </label>
            <div className="row-actions full">
              <button className="btn btn-primary" type="submit" disabled={busy}>
                Save
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
          </form>
        )}
        <div className="panel table-wrap">
          <table>
            <thead>
              <tr>
                <th>Logo</th>
                <th>Name</th>
                <th>Slug</th>
                <th>Sort</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.recordName}>
                  <td>
                    {row.logoURL ? (
                      <>
                        <img className="catalog-logo network-logo" src={row.logoURL} alt="" />
                        {isVectorAssetURL(row.logoURL) && (
                          <span className="badge warn" title="The app cannot decode SVG. Re-cache this icon to publish a PNG.">
                            SVG · blank in app
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>{row.name}</td>
                  <td>
                    <code>{row.slug}</code>
                  </td>
                  <td>{row.sortOrder}</td>
                  <td className="row-actions">
                    {isVectorAssetURL(row.logoURL) && (
                      <button
                        className="btn btn-brass"
                        type="button"
                        disabled={busy}
                        onClick={() => void convertExistingLogo(row)}
                      >
                        {busy ? "Converting…" : "Convert to PNG"}
                      </button>
                    )}
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => setEditing(row)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() =>
                        void deleteNetwork(row.recordName)
                          .then(reload)
                          .catch((err) =>
                            setError(err instanceof Error ? err.message : "Delete failed"),
                          )
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </AppShell>
  );
}
